class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  // The A/B flag. Evaluated fresh on EVERY call — never cached, never read in
  // the constructor. Intelligems adds `sr-search-v2` to <body> after this
  // element has upgraded; the user cannot type before the class lands, so a
  // per-call read makes the race impossible. Scoped to the header modal: the
  // /search page's own <predictive-search> keeps Dawn behaviour in both arms.
  isV2() {
    return document.body.classList.contains('sr-search-v2') && this.closest('.search-modal') !== null;
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    // Control: Dawn suppresses submit while a virtual option is selected, because
    // focus stays in the input and Enter would both activate it and submit.
    // v2: focus is real, Enter on a result is the browser following that link and
    // never reaches this handler.
    if (!this.isV2() && this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  // v2 only: the input is not a combobox — the panel is a region of ordinary
  // links, not a listbox. Control keeps every attribute untouched.
  stripComboboxAria() {
    ['role', 'aria-expanded', 'aria-owns', 'aria-controls', 'aria-haspopup', 'aria-autocomplete']
      .forEach((name) => this.input.removeAttribute(name));
  }

  onFocus() {
    if (this.isV2()) this.stripComboboxAria();

    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      // v2: the dropdown is part of the search modal and stays up while the
      // modal is open; closing is the modal's job (✕, Esc, outside click).
      if (this.isV2()) {
        const modal = this.closest('details');
        if (modal && modal.hasAttribute('open')) return;
      }

      this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        // v2 has no Enter case: results are links, the browser activates the focused one.
        if (!this.isV2()) this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (this.isV2()) {
      this.switchOptionV2(direction);
      return;
    }

    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  // v2: every visible link in the panel, DOM order. offsetParent is null inside a
  // display:none panel, so the results panel and the default panel are never
  // both reachable — and arrow traversal matches the Tab order exactly.
  getNavigableLinks() {
    return Array.prototype.filter.call(
      this.querySelectorAll('.predictive-search a[href]'),
      (link) => link.offsetParent !== null
    );
  }

  // v2: arrow keys move REAL focus. No aria-activedescendant, no aria-selected.
  // Index traversal because results are several <ul>s; wraps at both ends;
  // ArrowUp from the input jumps to the last link.
  switchOptionV2(direction) {
    const links = this.getNavigableLinks();
    if (!links.length) return;

    const moveUp = direction === 'up';
    const currentIndex = links.indexOf(document.activeElement);

    let nextIndex;
    if (currentIndex === -1) {
      nextIndex = moveUp ? links.length - 1 : 0;
    } else {
      nextIndex = moveUp ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = links.length - 1;
      if (nextIndex >= links.length) nextIndex = 0;
    }

    links[nextIndex].focus();
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const v2 = this.isV2();
    // Cache keyed by arm so a control response is never painted into v2 or vice versa.
    const queryKey = (v2 ? 'v2:' : 'v1:') + searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Control: Dawn's request, byte-for-byte. v2: three resource types with
    // limit_scope=each (default `all` spreads the 4 across every type combined),
    // rendered by sections/sr-predictive-search.liquid.
    const url = v2
      ? `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=query,collection,product&${encodeURIComponent('resources[limit]')}=4&${encodeURIComponent('resources[limit_scope]')}=each&section_id=sr-predictive-search`
      : `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`;
    const sectionSelector = v2 ? '#shopify-section-sr-predictive-search' : '#shopify-section-predictive-search';

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          // In v2 a stale [results] attribute would hide the default panel and
          // the results panel simultaneously; clearing it brings the curated
          // default panel back instead of leaving a blank one.
          this.close();
          if (this.isV2()) this.removeAttribute('results');
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector(sectionSelector).innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // In v2 a stale [results] attribute would hide the default panel and
        // the results panel simultaneously; clearing it brings the curated
        // default panel back instead of leaving a blank one.
        this.close();
        if (this.isV2()) this.removeAttribute('results');
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    // Analytics hook (src/js/sr-search-tracking.js). Fired after the markup is
    // in the DOM so listeners can count the rendered rows.
    this.dispatchEvent(new CustomEvent('sr:search:render', { bubbles: true, detail: { term: this.getQuery() } }));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    // v2 measures from the search bar: on mobile the bar is fixed at top:0 over
    // the announcement bars, so the header section's bottom is ~113px too low.
    const bar = (this.isV2() && this.closest('.search-modal')) || document.getElementById('shopify-section-header');
    this.resultsMaxHeight = window.innerHeight - bar.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    if (this.isV2()) {
      this.stripComboboxAria();
    } else {
      this.input.setAttribute('aria-expanded', true);
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    if (this.isV2()) {
      // The v2 CSS hides the curated default panel while [loading] is set, and
      // Dawn only clears it on the success path — a failed fetch would spin forever.
      this.removeAttribute('loading');
    } else {
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', false);
    }

    this.removeAttribute('open');
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
